import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  FileText,
  Target,
  Calendar,
  Activity,
  CheckCircle2,
  Circle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Edit,
  User,
  ExternalLink,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  TreatmentPlan,
  SubscriptionPlan,
  ConsultationHistoryEntry,
  getGoalStatusColor,
  getGoalStatusLabel,
  getVitalTypeLabel,
  getVitalTypeUnit,
} from "@/lib/dashboard/treatmentPlandata";
import { Appointment } from "@/lib/dashboard/appointmentsData";
import { Consultation } from "@/lib/dashboard/consultationsData";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { useTitanApp } from "@/hooks/useTitanApp";
import { getTargetDirectionSymbol } from "@/lib/treatmentPlan/treatmentPlanData";

interface PatientTreatmentPlansProps {
  treatmentPlans: TreatmentPlan[];
  isDarkMode: boolean;
  isLoading?: boolean;
  onStartConsultation?: (treatmentPlanId: string) => void;
  onEditTreatmentPlan?: (treatmentPlan: TreatmentPlan) => void;
  appointments?: Appointment[];
  consultations?: Consultation[];
  vitals?: any[];
  onVitalClick?: (vitalType: string) => void;
}

export function PatientTreatmentPlans({
  treatmentPlans,
  isDarkMode,
  isLoading = false,
  onStartConsultation,
  onEditTreatmentPlan,
  appointments = [],
  consultations = [],
  vitals = [],
  onVitalClick,
}: PatientTreatmentPlansProps) {
  const { pharmacyServiceState } = useTitanApp();
  const { defaultPharmacyServices = [] } = pharmacyServiceState || {};

  const getServiceNameById = (serviceId?: string) => {
    if (!serviceId) return "";

    const service = (defaultPharmacyServices || []).find(
      (s: any) => String(s?._id) === String(serviceId),
    );

    return service?.serviceName || service?.name || "";
  };

  const normalizeGoalVitalType = (type?: string) => {
    if (!type) return type;
    const normalized = String(type).toLowerCase();
    const map: Record<string, string> = {
      blood_pressure: "blood-pressure",
      heart_rate: "heart-rate",
      blood_glucose: "blood-sugar",
      oxygen_saturation: "oxygen-saturation",
      respiratory_rate: "respiratory-rate",
      news_pain_score: "news-pain-score",
    };
    return map[normalized] ?? normalized;
  };

  const originalPlanMap = React.useMemo(() => {
    const map = new Map<string, any>();
    treatmentPlans?.forEach((tp: any) => {
      const key = tp.treatmentPlanId ?? tp.id;
      if (key) map.set(key, tp);
    });
    return map;
  }, [treatmentPlans]);

  const [expandedPlans, setExpandedPlans] = useState<Set<string>>(new Set());
  const [selectedAppointmentConsultation, setSelectedAppointmentConsultation] =
    useState<Consultation | null>(null);

  const togglePlan = (planId: string) => {
    const newExpanded = new Set(expandedPlans);
    if (newExpanded.has(planId)) {
      newExpanded.delete(planId);
    } else {
      newExpanded.add(planId);
    }
    setExpandedPlans(newExpanded);
  };
  // Helper to map eventStatus number to status string
  const mapEventStatus = (eventStatus: number): Appointment["status"] => {
    switch (eventStatus) {
      case 1:
        return "Completed";
      case 2:
        return "Pending";
      case 3:
        return "Active";
      case 4:
        return "Rescheduled";
      default:
        return "Pending";
    }
  };

  // The backend's get-patient-active-treatment-plan endpoint returns follow-up
  // appointments in schedule-event shape (startDate/endDate only, no literal
  // date/time fields), and its startDate is stored with Mongo Kind=Unspecified
  // so the API response omits the trailing 'Z'. The date/time digits themselves
  // are never altered though — only the 'Z' marker is missing — so, exactly like
  // FollowUpSection's getStoredDateTimeParts, extract the digits directly via
  // regex and never hand this string to `new Date(...)`, which would silently
  // reinterpret it in the viewing browser's own local timezone.
  const getStoredDateTimeParts = (
    value?: string,
  ): { date: string; time: string } => {
    const match = String(value || "").match(
      /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/,
    );
    return { date: match?.[1] || "", time: match?.[2] || "" };
  };

  // Helper to format appointment from API object
  const formatAppointmentFromApi = (appt: any): Appointment => {
    // Prefer the plain `date`/`time` strings if the backend ever includes them
    // directly on the appointment.
    let rawDate = appt.date || "";
    let rawTime = appt.time || "";

    if ((!rawDate || !rawTime) && appt.startDate) {
      const stored = getStoredDateTimeParts(appt.startDate);
      rawDate = rawDate || stored.date;
      rawTime = rawTime || stored.time;
    }

    const [year, month, day] = rawDate ? rawDate.split("-") : [];
    const date =
      year && month && day
        ? `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`
        : "";
    const time = rawTime;

    return {
      id: appt.id || appt._id || "",
      date,
      time,
      type:
        getServiceNameById(appt.serviceID || appt.serviceId) ||
        appt.serviceName ||
        appt.serviceType ||
        appt.name ||
        "Appointment",
      status: mapEventStatus(appt.eventStatus),
      practitionerName: appt.resourceId || appt.pharmacist || "",
      patientConsultaitonId: appt.consultationID || appt.consultationId || "",
      patientID: appt.patientID || appt.patientId || "",
      eventStatus: appt.eventStatus,
    };
  };

  // Helper to get next appointment from full objects or IDs
  const getNextAppointment = (appointmentData: any[]) => {
    if (!appointmentData || appointmentData.length === 0) return null;

    // Check if we have full objects or IDs
    const appts = appointmentData.map((item: any) => {
      if (typeof item === "string") {
        // It's an ID, look it up in appointments prop
        return appointments.find((apt) => apt?.id?.toString() === item);
      }
      // It's a full object, format it
      return formatAppointmentFromApi(item);
    });

    const upcoming = appts
      .filter((apt): apt is Appointment => apt !== undefined)
      .filter((apt) => apt.status !== "Completed")
      .sort((a, b) => {
        const dateA = new Date(a.date.split("/").reverse().join("-"));
        const dateB = new Date(b.date.split("/").reverse().join("-"));
        return dateA.getTime() - dateB.getTime();
      });

    return upcoming[0] || null;
  };

  const transformedPlans = treatmentPlans?.map((plan: any) => ({
    id: plan.treatmentPlanId ?? plan.id,
    name: plan.name,
    description: plan.description,
    status:
      ({ 1: "active", 2: "completed", 3: "paused", 4: "cancelled" } as any)[
        plan.status
      ] ?? String(plan.status || "").toLowerCase(),
    startDate: plan.startDate,
    endDate: plan.endDate,
    createdAt: plan.createdAt,
    goals: (plan.goals ?? []).map((goal: any) => ({
      id: goal.id,
      description: goal.description,
      linkedVitalType: (() => {
        if (goal.linkedVitalType == null) return undefined;
        const mapped = (() => {
          const numericMap: Record<number, string> = {
            1: "weight",
            2: "blood_pressure",
            3: "heart_rate",
            4: "temperature",
            5: "height",
            6: "bmi",
            7: "oxygen_saturation",
            8: "blood_glucose",
          };

          const stringMap: Record<string, string> = {
            weight: "weight",
            bloodpressure: "blood_pressure",
            blood_pressure: "blood_pressure",
            heartrate: "heart_rate",
            heart_rate: "heart_rate",
            temperature: "temperature",
            height: "height",
            bmi: "bmi",
            oxygensaturation: "oxygen_saturation",
            oxygen_saturation: "oxygen_saturation",
            bloodglucose: "blood_glucose",
            blood_glucose: "blood_glucose",
          };

          if (typeof goal.linkedVitalType === "number") {
            return (
              numericMap[goal.linkedVitalType] ?? String(goal.linkedVitalType)
            );
          }

          const key = String(goal.linkedVitalType)
            .toLowerCase()
            .replace(/\s+/g, "_");
          return stringMap[key] ?? key;
        })();
        return normalizeGoalVitalType(mapped);
      })(),
      targetDirection:
        typeof goal.targetDirection === "number"
          ? (({ 1: "AtOrAbove", 2: "AtOrBelow", 3: "EqualTo" } as any)[
              goal.targetDirection
            ] ?? "AtOrBelow")
          : (goal.targetDirection ?? undefined),
      targetValue: goal.targetValue,
      targetDate: goal.targetDate,
      status:
        (
          {
            1: "pending",
            2: "in-progress",
            3: "achieved",
            4: "abandoned",
          } as any
        )[goal.status] ?? goal.status,

      notes: goal.notes,
    })),
    followUpAppointments: plan.followUpAppointments ?? [],
    linkedPrescriptions: plan.linkedPrescriptions ?? [],
    consultationHistory: plan.consultationHistory ?? [],
    subscriptionPlan: plan.subscriptionPlan ?? undefined,
  }));

  const activePlans = transformedPlans?.filter(
    (plan) => plan.status === "active",
  );

  if (isLoading) {
    return (
      <div
        className={`rounded-lg border transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-800 border-gray-700"
            : "bg-white border-gray-200"
        } mb-6 mt-6 flex items-center justify-center py-12`}
      >
        <div className="flex flex-col items-center gap-3">
          <svg
            className={`animate-spin h-8 w-8 ${
              isDarkMode ? "text-purple-400" : "text-purple-600"
            }`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p
            className={`text-sm ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Loading treatment plans...
          </p>
        </div>
      </div>
    );
  }

  if (!activePlans || activePlans.length === 0) {
    return null;
  }
  const getStatusColor = (status: TreatmentPlan["status"]) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "completed":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
      case "paused":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
      case "cancelled":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    
    const dateStr = date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    
    // Extract time directly from ISO string to show UTC time as-is
    const timeStr = dateString.substring(11, 16);
    
    return `${dateStr} ${timeStr}`;
  };

  const getGoalIcon = (status: string) => {
    switch (status) {
      case "achieved":
        return (
          <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
        );
      case "in-progress":
        return <Circle className="w-4 h-4 text-blue-600 dark:text-blue-400" />;
      case "pending":
        return <Circle className="w-4 h-4 text-gray-400" />;
      case "abandoned":
        return (
          <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
        );
      default:
        return <Circle className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div
      className={`rounded-lg border transition-colors duration-300 ${
        isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      } mb-6 mt-6`}
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText
              className={`w-5 h-5 ${isDarkMode ? "text-purple-400" : "text-purple-600"}`}
            />
            <h3
              className={`text-lg font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Active Treatment Plans
            </h3>
          </div>
          <Badge
            variant="secondary"
            className={isDarkMode ? "bg-gray-700" : ""}
          >
            {activePlans.length} {activePlans.length === 1 ? "plan" : "plans"}
          </Badge>
        </div>

        <div className="space-y-3">
          {activePlans.map((plan) => {
            const isExpanded = expandedPlans.has(plan.id);
            const totalGoals = plan.goals?.length || 0;
            const achievedGoals =
              plan.goals?.filter((g) => g.status === "achieved") || [];
            const goalsWithVitals =
              plan.goals?.filter((g) => g.linkedVitalType) || [];

            return (
              <div
                key={plan.id}
                className={`rounded-lg border transition-colors duration-300 ${
                  isDarkMode
                    ? "bg-gray-900 border-gray-700"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <Collapsible
                  open={isExpanded}
                  onOpenChange={() => togglePlan(plan.id)}
                >
                  <div className="p-4">
                    {/* Header Section */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4
                            className={`font-semibold ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {plan.name}
                          </h4>
                          <Badge className={getStatusColor(plan.status)}>
                            {plan.status.charAt(0).toUpperCase() +
                              plan.status.slice(1)}
                          </Badge>
                        </div>
                        {plan.description && (
                          <p
                            className={`text-sm ${
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            } mb-2`}
                          >
                            {plan.description}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {onEditTreatmentPlan && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Pass the ORIGINAL raw plan so all fields (serviceType, date,
                              // time, notes, vaccinesAdminister, tasksToComplete, etc.) are intact
                              const originalPlan = originalPlanMap.get(plan.id);
                              onEditTreatmentPlan(originalPlan ?? plan);
                            }}
                            className={
                              isDarkMode ? "text-gray-400 hover:text-white" : ""
                            }
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        )}
                        <CollapsibleTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={
                              isDarkMode ? "text-gray-400 hover:text-white" : ""
                            }
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                    </div>

                    {/* Quick Summary Stats */}
                    <div
                      className={cn(
                        "grid gap-4 mb-3",
                        onStartConsultation ? "grid-cols-4" : "grid-cols-3",
                      )}
                    >
                      <div>
                        <p
                          className={`text-xs ${
                            isDarkMode ? "text-gray-500" : "text-gray-500"
                          } mb-1`}
                        >
                          Goals Progress
                        </p>
                        <div className="flex items-center gap-2">
                          <Target className="w-3.5 h-3.5 text-blue-600" />
                          <p
                            className={`text-sm font-medium ${
                              isDarkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            {achievedGoals.length}/{totalGoals}
                          </p>
                        </div>
                        {totalGoals > 0 && (
                          <div className="mt-1 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                            <div
                              className="bg-blue-600 h-1.5 rounded-full transition-all"
                              style={{
                                width: `${(achievedGoals.length / totalGoals) * 100}%`,
                              }}
                            />
                          </div>
                        )}
                      </div>

                      <div>
                        <p
                          className={`text-xs ${
                            isDarkMode ? "text-gray-500" : "text-gray-500"
                          } mb-1`}
                        >
                          Next Appointment
                        </p>
                        {(() => {
                          const nextAppt = getNextAppointment(
                            plan.followUpAppointments || [],
                          );
                          return nextAppt ? (
                            <div>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-3.5 h-3.5 text-green-600" />
                                <p
                                  className={`text-xs font-medium ${
                                    isDarkMode
                                      ? "text-gray-300"
                                      : "text-gray-700"
                                  }`}
                                >
                                  {nextAppt.date}
                                </p>
                              </div>
                              <p
                                className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                              >
                                {nextAppt.time} - {nextAppt.type}
                              </p>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <Calendar className="w-3.5 h-3.5 text-gray-400" />
                              <p
                                className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                              >
                                None
                              </p>
                            </div>
                          );
                        })()}
                      </div>

                      <div>
                        <p
                          className={`text-xs ${
                            isDarkMode ? "text-gray-500" : "text-gray-500"
                          } mb-1`}
                        >
                          Consultations
                        </p>
                        <div className="flex items-center gap-2">
                          <User className="w-3.5 h-3.5 text-orange-600" />
                          <p
                            className={`text-sm font-medium ${
                              isDarkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            {plan.consultationHistory?.filter(
                              (c: any) => c.isConsultationCompleted,
                            )?.length || 0}
                            /{plan.consultationHistory?.length || 0} done
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details with Tables */}
                    <CollapsibleContent className="space-y-2">
                      {/* Plan Overview Table */}
                      <div className="overflow-x-auto">
                        <table
                          className={`w-full text-sm border-collapse ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}
                        >
                          <thead>
                            <tr
                              className={
                                isDarkMode ? "bg-gray-700/50" : "bg-gray-100"
                              }
                            >
                              <th
                                className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                colSpan={2}
                              >
                                📋 Plan Overview
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              className={
                                isDarkMode
                                  ? "hover:bg-gray-700/30"
                                  : "hover:bg-gray-50"
                              }
                            >
                              <td
                                className={`border px-2 py-1 font-medium ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                              >
                                Status
                              </td>
                              <td
                                className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                              >
                                <Badge className={getStatusColor(plan.status)}>
                                  {plan.status.charAt(0).toUpperCase() +
                                    plan.status.slice(1)}
                                </Badge>
                              </td>
                            </tr>
                            {plan.startDate && (
                              <tr
                                className={
                                  isDarkMode
                                    ? "hover:bg-gray-700/30"
                                    : "hover:bg-gray-50"
                                }
                              >
                                <td
                                  className={`border px-2 py-1 font-medium ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                >
                                  Start Date
                                </td>
                                <td
                                  className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                >
                                  {formatDate(plan.startDate)}
                                </td>
                              </tr>
                            )}
                            {plan.endDate && (
                              <tr
                                className={
                                  isDarkMode
                                    ? "hover:bg-gray-700/30"
                                    : "hover:bg-gray-50"
                                }
                              >
                                <td
                                  className={`border px-2 py-1 font-medium ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                >
                                  End Date
                                </td>
                                <td
                                  className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                >
                                  {formatDate(plan.endDate)}
                                </td>
                              </tr>
                            )}
                            <tr
                              className={
                                isDarkMode
                                  ? "hover:bg-gray-700/30"
                                  : "hover:bg-gray-50"
                              }
                            >
                              <td
                                className={`border px-2 py-1 font-medium ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                              >
                                Created
                              </td>
                              <td
                                className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                              >
                                {formatDate(plan.createdAt)}
                              </td>
                            </tr>
                            {plan.consultationHistory &&
                              plan.consultationHistory.length > 0 && (
                                <tr
                                  className={
                                    isDarkMode
                                      ? "hover:bg-gray-700/30"
                                      : "hover:bg-gray-50"
                                  }
                                >
                                  <td
                                    className={`border px-2 py-1 font-medium ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                  >
                                    Consultations
                                  </td>
                                  <td
                                    className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                  >
                                    {plan.consultationHistory.length}
                                  </td>
                                </tr>
                              )}
                          </tbody>
                        </table>
                      </div>

                      {/* Goals Table */}
                      {plan.goals && plan.goals.length > 0 && (
                        <div className="overflow-x-auto">
                          <table
                            className={`w-full text-sm border-collapse ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}
                          >
                            <thead>
                              <tr
                                className={
                                  isDarkMode ? "bg-gray-700/50" : "bg-gray-100"
                                }
                              >
                                <th
                                  className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                  colSpan={5}
                                >
                                  🎯 Clinical Goals ({totalGoals})
                                </th>
                              </tr>
                              <tr
                                className={
                                  isDarkMode ? "bg-gray-700/30" : "bg-gray-50"
                                }
                              >
                                <th
                                  className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                >
                                  Goal
                                </th>
                                <th
                                  className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                >
                                  Tracking
                                </th>
                                <th
                                  className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                >
                                  Target
                                </th>
                                <th
                                  className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                >
                                  Due Date
                                </th>
                                <th
                                  className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                >
                                  Status
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {plan.goals.map((goal) => {
                                const hasVital = !!goal.linkedVitalType;
                                const latestVital =
                                  hasVital && vitals.length > 0
                                    ? vitals
                                        .filter(
                                          (v) =>
                                            normalizeGoalVitalType(v.type) ===
                                            goal.linkedVitalType,
                                        )
                                        .sort(
                                          (a, b) =>
                                            new Date(b.timestamp).getTime() -
                                            new Date(a.timestamp).getTime(),
                                        )[0]
                                    : null;

                                return (
                                  <tr
                                    key={goal.id}
                                    className={cn(
                                      isDarkMode
                                        ? "hover:bg-gray-700/30"
                                        : "hover:bg-gray-50",
                                      hasVital &&
                                        onVitalClick &&
                                        "cursor-pointer",
                                    )}
                                    onClick={() => {
                                      if (
                                        hasVital &&
                                        onVitalClick &&
                                        goal.linkedVitalType
                                      ) {
                                        onVitalClick(goal.linkedVitalType);
                                      }
                                    }}
                                  >
                                    <td
                                      className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                    >
                                      <div className="flex items-center gap-2">
                                        {getGoalIcon(goal.status)}
                                        <span>{goal.description}</span>
                                      </div>
                                    </td>
                                    <td
                                      className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                    >
                                      {goal.linkedVitalType ? (
                                        <div className="flex items-center gap-1.5">
                                          <Activity className="w-3 h-3 text-blue-600 flex-shrink-0" />
                                          <span className="font-medium">
                                            {getVitalTypeLabel(
                                              goal.linkedVitalType,
                                            )}
                                          </span>
                                          {latestVital && (
                                            <Badge
                                              variant="outline"
                                              className={cn(
                                                "text-[10px] px-1 py-0 ml-1",
                                                isDarkMode
                                                  ? "bg-blue-900/30 text-blue-400 border-blue-700"
                                                  : "bg-blue-50 text-blue-700 border-blue-200",
                                              )}
                                            >
                                              Latest: {latestVital.value}
                                              {getVitalTypeUnit(
                                                goal.linkedVitalType,
                                              )}
                                            </Badge>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-xs italic opacity-60">
                                          No vital tracking
                                        </span>
                                      )}
                                    </td>
                                    <td
                                      className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                    >
                                      {goal.targetDirection && (
                                        <span className="mr-0.5">
                                          {getTargetDirectionSymbol(
                                            goal.targetDirection,
                                          )}
                                        </span>
                                      )}
                                      {goal.targetValue}
                                      {goal.linkedVitalType && (
                                        <span className="ml-0.5 text-xs">
                                          {getVitalTypeUnit(
                                            goal.linkedVitalType,
                                          )}
                                        </span>
                                      )}
                                    </td>
                                    <td
                                      className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                    >
                                      {goal.targetDate
                                        ? formatDate(goal.targetDate)
                                        : "No target date"}
                                    </td>
                                    <td
                                      className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                    >
                                      <Badge
                                        className={`text-xs px-1.5 py-0 ${getGoalStatusColor(goal.status)}`}
                                        variant="outline"
                                      >
                                        {getGoalStatusLabel(goal.status)}
                                      </Badge>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Follow-up Appointments Table */}
                      {(plan.followUpAppointments?.length > 0 ||
                        plan.consultationHistory?.length > 0) &&
                        (() => {
                          const parseFollowUpDate = (date?: string) => {
                            if (!date) return Number.POSITIVE_INFINITY;

                            const [day, month, year] = date.split("/");
                            if (!day || !month || !year) {
                              return Number.POSITIVE_INFINITY;
                            }

                            const parsedDate = new Date(
                              `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`,
                            );

                            return Number.isNaN(parsedDate.getTime())
                              ? Number.POSITIVE_INFINITY
                              : parsedDate.getTime();
                          };
                          // Format appointment data - handle both IDs and full objects
                          const appointmentDetails = (
                            plan.followUpAppointments || []
                          )
                            .map((item: any) => {
                              if (typeof item === "string") {
                                return appointments.find(
                                  (apt) => apt?.id?.toString() === item,
                                );
                              }
                              return formatAppointmentFromApi(item);
                            })
                            .filter(
                              (apt): apt is Appointment => apt !== undefined,
                            )
                            .sort(
                              (a, b) =>
                                parseFollowUpDate(a.date) -
                                parseFollowUpDate(b.date),
                            );

                          // Find consultations that are NOT linked to any appointment
                          const linkedConsultationIds = new Set(
                            appointmentDetails
                              .filter((apt) => apt.patientConsultaitonId)
                              .map((apt) => apt.patientConsultaitonId),
                          );

                          // const unscheduledConsultations = (
                          //   plan.consultationHistory || []
                          // ).filter((consult) => {
                          //   // Check if this consultation is linked to any appointment
                          //   const consultId =
                          //     parseInt(consult.id.replace(/\D/g, "")) || 0;
                          //   return !linkedConsultationIds.has(consultId);
                          // });

                          // const totalItems =
                          //   appointmentDetails.length +
                          //   unscheduledConsultations.length;
                          // if (totalItems === 0) return null;

                          const totalItems = appointmentDetails.length;
                          if (totalItems === 0) return null;

                          return (
                            <div className="overflow-x-auto">
                              <table
                                className={`w-full text-sm border-collapse ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}
                              >
                                <thead>
                                  <tr
                                    className={
                                      isDarkMode
                                        ? "bg-gray-700/50"
                                        : "bg-gray-100"
                                    }
                                  >
                                    <th
                                      className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                      colSpan={4}
                                    >
                                      📅 Follow-up Appointments & Consultations
                                      ({totalItems})
                                    </th>
                                  </tr>
                                  <tr
                                    className={
                                      isDarkMode
                                        ? "bg-gray-700/30"
                                        : "bg-gray-50"
                                    }
                                  >
                                    <th
                                      className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                    >
                                      Date
                                    </th>
                                    <th
                                      className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                    >
                                      Time
                                    </th>
                                    <th
                                      className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                    >
                                      Type
                                    </th>
                                    <th
                                      className={`border px-2 py-1 text-left font-semibold ${isDarkMode ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}
                                    >
                                      Status
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {appointmentDetails.map((apt) => {
                                    const isCompleted =
                                      apt.status === "Completed";
                                    const linkedConsultation =
                                      apt.patientConsultaitonId
                                        ? consultations.find(
                                            (c) =>
                                              c.consultationId ===
                                              apt.patientConsultaitonId,
                                          )
                                        : null;
                                    const canViewConsultation =
                                      isCompleted && linkedConsultation;

                                    return (
                                      <tr
                                        key={`apt-${apt.id}`}
                                        className={cn(
                                          isDarkMode
                                            ? "hover:bg-gray-700/30"
                                            : "hover:bg-gray-50",
                                          canViewConsultation &&
                                            "cursor-pointer",
                                        )}
                                        onClick={() => {
                                          if (canViewConsultation) {
                                            setSelectedAppointmentConsultation(
                                              linkedConsultation,
                                            );
                                          }
                                        }}
                                      >
                                        <td
                                          className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                        >
                                          {apt.date}
                                        </td>
                                        <td
                                          className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                        >
                                          {apt.time}
                                        </td>
                                        <td
                                          className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                        >
                                          {apt.type}
                                        </td>
                                        <td
                                          className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                        >
                                          <div className="flex items-center gap-1.5">
                                            <Badge
                                              variant="outline"
                                              className={cn(
                                                "text-xs px-1.5 py-0",
                                                apt.status === "Pending" &&
                                                  "bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700",
                                                apt.status === "Active" &&
                                                  "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700",
                                                apt.status === "Completed" &&
                                                  "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700",
                                                apt.status === "Rescheduled" &&
                                                  "bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-700",
                                              )}
                                            >
                                              {apt.status}
                                            </Badge>
                                            {canViewConsultation && (
                                              <ExternalLink className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  {/* Unscheduled Consultations */}
                                  {/* {unscheduledConsultations.map((consult) => (
                                    <tr
                                      key={`consult-${consult.id}`}
                                      className={cn(
                                        isDarkMode
                                          ? "hover:bg-gray-700/30 bg-purple-900/10"
                                          : "hover:bg-gray-50 bg-purple-50",
                                        "cursor-pointer",
                                      )}
                                      onClick={() => {
                                        // Convert ConsultationHistoryEntry to Consultation format
                                        const consultationData: Consultation = {
                                          id:
                                            parseInt(
                                              consult.id.replace(/\D/g, ""),
                                            ) || 0,
                                          date: consult.date,
                                          time: consult.time,
                                          type: consult.type,
                                          pharmacist: consult.pharmacist,
                                          summary: consult.summary || "",
                                          actions: [],
                                        };
                                        setSelectedAppointmentConsultation(
                                          consultationData,
                                        );
                                      }}
                                    >
                                      <td
                                        className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                      >
                                        {consult.date}
                                      </td>
                                      <td
                                        className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                      >
                                        {consult.time}
                                      </td>
                                      <td
                                        className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                      >
                                        <div className="flex items-center gap-1.5">
                                          <span>{consult.type}</span>
                                          <Badge
                                            variant="outline"
                                            className="text-[10px] px-1 py-0 bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-700"
                                          >
                                            Unscheduled
                                          </Badge>
                                        </div>
                                      </td>
                                      <td
                                        className={`border px-2 py-1 ${isDarkMode ? "border-gray-600 text-gray-400" : "border-gray-300 text-gray-600"}`}
                                      >
                                        <div className="flex items-center gap-1.5">
                                          <Badge
                                            variant="outline"
                                            className="text-xs px-1.5 py-0 bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-700"
                                          >
                                            Completed
                                          </Badge>
                                          <ExternalLink className="w-3 h-3 text-purple-600 dark:text-purple-400" />
                                        </div>
                                      </td>
                                    </tr>
                                  ))} */}
                                </tbody>
                              </table>
                            </div>
                          );
                        })()}
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              </div>
            );
          })}
        </div>
      </div>

      {/* Appointment Consultation Details Modal */}
      <Dialog
        open={!!selectedAppointmentConsultation}
        onOpenChange={(open) =>
          !open && setSelectedAppointmentConsultation(null)
        }
      >
        <DialogContent
          className={cn(
            "max-w-2xl",
            isDarkMode ? "bg-gray-800 text-white border-gray-700" : "bg-white",
          )}
        >
          <DialogHeader>
            <DialogTitle className={isDarkMode ? "text-white" : ""}>
              Consultation Details
            </DialogTitle>
            <DialogDescription className={isDarkMode ? "text-gray-400" : ""}>
              {selectedAppointmentConsultation?.date} at{" "}
              {selectedAppointmentConsultation?.time}
            </DialogDescription>
          </DialogHeader>
          {selectedAppointmentConsultation && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Type
                  </p>
                  <p
                    className={`font-medium ${isDarkMode ? "text-gray-300" : "text-gray-900"}`}
                  >
                    {selectedAppointmentConsultation.type}
                  </p>
                </div>
                <div>
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"}`}
                  >
                    Pharmacist
                  </p>
                  <p
                    className={`font-medium ${isDarkMode ? "text-gray-300" : "text-gray-900"}`}
                  >
                    {selectedAppointmentConsultation.pharmacist}
                  </p>
                </div>
              </div>
              {selectedAppointmentConsultation.summary && (
                <div>
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"} mb-2`}
                  >
                    Summary
                  </p>
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                  >
                    {selectedAppointmentConsultation.summary}
                  </p>
                </div>
              )}
              {selectedAppointmentConsultation.actions &&
                selectedAppointmentConsultation.actions.length > 0 && (
                  <div>
                    <p
                      className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"} mb-2`}
                    >
                      Actions Taken
                    </p>
                    <div className="space-y-2">
                      {selectedAppointmentConsultation.actions.map(
                        (action, idx) => (
                          <div
                            key={idx}
                            className={cn(
                              "p-2 rounded-lg border",
                              isDarkMode
                                ? "bg-gray-700/50 border-gray-600"
                                : "bg-gray-50 border-gray-200",
                            )}
                          >
                            <div className="flex items-start justify-between">
                              <div>
                                <Badge
                                  variant="outline"
                                  className={cn(
                                    "text-xs mb-1",
                                    action.actionName === "prescription" &&
                                      "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400",
                                    action.actionName === "payment" &&
                                      "bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400",
                                    action.actionName === "referral" &&
                                      "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400",
                                    action.actionName === "vaccine" &&
                                      "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400",
                                    action.actionName === "appointment" &&
                                      "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400",
                                  )}
                                >
                                  {action.actionName.charAt(0).toUpperCase() +
                                    action.actionName.slice(1)}
                                </Badge>
                                <p
                                  className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                >
                                  {action.actionName}
                                </p>
                              </div>
                              {action.actionName && (
                                <p
                                  className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-900"}`}
                                >
                                  {action.actionName}
                                </p>
                              )}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
